import { IngestEvent, KromemoInitOptions, TrackEventInput, TrackViewInput, TrackErrorInput } from './types';
import { createQueue, EventQueue } from './queue';
import { getOrCreateDeviceId, detectCountry, detectOS } from './device';
import { sanitizePayload, nowTsMs } from './utils';

let queue: EventQueue | null = null;
let options: Required<Omit<KromemoInitOptions, 'endpointBase'>> & {
  deviceId: string;
  endpointBase?: string;
  os?: string;
  country?: string;
} | null = null;

// In-memory dedupe cache: key -> last send timestamp
const recentEventMap: Map<string, number> = new Map();

function makeDedupeKey(evt: Pick<IngestEvent, 'type' | 'name' | 'url'>): string {
  const t = evt.type || 'event';
  const n = evt.name || '';
  const u = evt.url || '';
  return `${t}|${n}|${u}`;
}

function shouldDropByDedupe(key: string, windowMs: number): boolean {
  if (!windowMs || windowMs <= 0) return false;
  const now = nowTsMs();
  const last = recentEventMap.get(key) || 0;
  if (now - last < windowMs) {
    return true;
  }
  recentEventMap.set(key, now);
  // Opportunistic cleanup to avoid unbounded growth
  if (recentEventMap.size > 1000) {
    const cutoff = now - Math.max(windowMs * 10, 30_000);
    recentEventMap.forEach((ts, k) => {
      if (ts < cutoff) recentEventMap.delete(k);
    });
  }
  return false;
}

function ensureInitialized(): void {
  if (!options || !queue) {
    throw new Error('kromemo SDK is not initialized. Call init(...) first.');
  }
}

function buildEventBase(): Pick<IngestEvent, 'device_id' | 'url' | 'referrer' | 'os' | 'country' | 'ts'> {
  ensureInitialized();
  return {
    device_id: options!.deviceId,
    url: typeof location !== 'undefined' ? location.href : undefined,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    os: options!.os,
    country: options!.country,
    ts: nowTsMs(),
  };
}

export const kromemo = {
  init(initOptions: KromemoInitOptions) {
    if (!initOptions || !initOptions.projectId || !initOptions.apiKey) {
      throw new Error('init requires projectId and apiKey');
    }

    const deviceId = getOrCreateDeviceId();

    options = {
      projectId: initOptions.projectId,
      apiKey: initOptions.apiKey,
      endpointBase: initOptions.endpointBase,
      deviceId,
      maxBatchSize: initOptions.maxBatchSize ?? 100,
      maxQueueBytes: initOptions.maxQueueBytes ?? 500_000,
      flushIntervalMs: initOptions.flushIntervalMs ?? 5000,
      retryMaxAttempts: initOptions.retryMaxAttempts ?? 3,
      os: detectOS(),
      country: detectCountry(),
      dedupeWindowMs: initOptions.dedupeWindowMs ?? 0,
    };

    // Create queue
    queue = createQueue({
      projectId: options.projectId,
      apiKey: options.apiKey,
      endpointBase: options.endpointBase,
      maxBatchSize: options.maxBatchSize,
      maxQueueBytes: options.maxQueueBytes,
      flushIntervalMs: options.flushIntervalMs,
      retryMaxAttempts: options.retryMaxAttempts,
    });

    return { deviceId };
  },

  // Only object form
  trackEvent(input: TrackEventInput) {
    ensureInitialized();
    const name = input.name;
    const ts = input.ts;
    const pl = input.payload;

    const evt: IngestEvent = {
      ...buildEventBase(),
      name,
      type: 'event',
      ...(ts ? { ts } : {}),
      payload: sanitizePayload(pl),
    };
    // Dedupe by type|name|url within window
    const key = makeDedupeKey(evt);
    if (shouldDropByDedupe(key, options!.dedupeWindowMs)) return;
    queue!.enqueue(evt);
  },

  // Only object form (input optional for auto-capture initial view)
  trackView(input?: TrackViewInput) {
    ensureInitialized();
    const name = input?.name ?? 'view';
    const url = input?.url || (typeof location !== 'undefined' ? location.href : undefined);
    const pl = input?.payload;

    const evt: IngestEvent = {
      ...buildEventBase(),
      name,
      type: 'view',
      url,
      payload: sanitizePayload(pl),
    };
    const key = makeDedupeKey(evt);
    if (shouldDropByDedupe(key, options!.dedupeWindowMs)) return;
    queue!.enqueue(evt);
  },

  // Only object form
  trackError(input: TrackErrorInput) {
    ensureInitialized();
    const errVal = input.error;
    const pl = input.payload;

    const basePayload = typeof errVal === 'string'
      ? { message: errVal }
      : { message: errVal.message, name: errVal.name, stack: errVal.stack };
    const evt: IngestEvent = {
      ...buildEventBase(),
      name: 'error',
      type: 'error',
      payload: sanitizePayload({ ...basePayload, ...pl }),
    };
    const key = makeDedupeKey(evt);
    if (shouldDropByDedupe(key, options!.dedupeWindowMs)) return;
    queue!.enqueue(evt);
  },

  flush() {
    ensureInitialized();
    return queue!.flush();
  },

  shutdown() {
    // for cleanup if needed
    if (queue) queue.stop();
    queue = null;
    options = null;
  },
};

export default kromemo;
